import { api, download, query } from "../api";
import type { EventsPage } from "../types";
import { actionButton, dateTime, debounce, detailBox, field, h, input, nodeLink, pager, select, show, statusChip, table, td, th, unixOf, userLink } from "../ui";

const LIMIT = 100;

export async function logsPage() {
  // Filters come from the URL (other pages link here), and go back into it.
  const u = new URLSearchParams(location.search);
  const st = { kind: u.get("kind") ?? "", level: u.get("level") ?? "", relay: u.get("relay") ?? "", account: u.get("account") ?? "", q: u.get("q") ?? "", from: u.get("from") ?? "", to: u.get("to") ?? "", offset: Number(u.get("offset")) || 0 };
  const params = () => ({ kind: st.kind, level: st.level, relay: st.relay, account: st.account, q: st.q, from: unixOf(st.from) || undefined, to: unixOf(st.to) || undefined, limit: LIMIT, offset: st.offset });
  const f = {
    kind: input({ value: st.kind, placeholder: "admin.* or relay.warning", autocomplete: "off", list: "kinds" }),
    level: select({}, [["", "Any level"], ["info", "info"], ["warn", "warn"], ["error", "error"]], st.level),
    relay: input({ value: st.relay, placeholder: "LA", maxlength: "16", autocomplete: "off" }),
    account: input({ value: st.account, placeholder: "acct_… (about it or by it)", autocomplete: "off" }),
    q: input({ type: "search", value: st.q, placeholder: "Words in the message or detail" }),
    from: input({ type: "datetime-local", value: st.from }),
    to: input({ type: "datetime-local", value: st.to }),
  };
  const kinds = h("datalist", { id: "kinds" }, ...["admin.*", "admin.signin", "admin.grant", "admin.node_*", "admin.routing_*", "admin.staff_*", "alarm.*", "alarm.fire", "alarm.clear", "block.*", "order.*", "order.paid", "relay.*", "relay.enrolled", "relay.warning", "http.error"].map((k) => h("option", { value: k })));
  const holder = h("div");
  const load = async () => {
    const p = params();
    const url = new URL(location.href);
    for (const [k, v] of Object.entries({ ...st, offset: st.offset || "" })) {
      if (v) url.searchParams.set(k, String(v));
      else url.searchParams.delete(k);
    }
    history.replaceState(null, "", url);
    const r = await api<EventsPage>(`/v1/admin/events?${query(p)}`);
    if (!r) return;
    holder.replaceChildren(
      table(
        [th("Time", "num"), th("Kind"), th("Level"), th("Actor"), th("Subject"), th("Node"), th("Message", "wrap"), th("Detail")],
        r.events.map((e) =>
          h(
            "tr",
            null,
            td(dateTime(e.t), "num"),
            td(e.kind, "mono"),
            h("td", null, statusChip(e.level)),
            h("td", null, e.actor.startsWith("acct_") ? userLink(e.actor, e.actor.slice(0, 12)) : e.actor || "—"),
            h("td", null, e.subject_kind === "account" && e.subject_id ? userLink(e.subject_id, e.subject_id.slice(0, 12)) : e.subject_kind === "relay" && e.subject_id ? nodeLink(e.subject_id) : e.subject_id ? `${e.subject_kind} ${e.subject_id.slice(0, 16)}` : "—"),
            h("td", null, e.relay ? nodeLink(e.relay) : "—"),
            td(e.message, "wrap"),
            h("td", null, detailBox(e.detail)),
          ),
        ),
        "No events match.",
      ),
      pager(r.total, r.limit, r.offset, (o) => ((st.offset = o), void load())),
    );
  };
  const refilter = () => {
    st.kind = f.kind.value.trim();
    st.level = f.level.value;
    st.relay = f.relay.value.trim().toUpperCase();
    st.account = f.account.value.trim();
    st.q = f.q.value.trim();
    st.from = f.from.value;
    st.to = f.to.value;
    st.offset = 0;
    void load();
  };
  const slow = debounce(refilter, 350);
  for (const el of [f.kind, f.relay, f.account, f.q]) el.addEventListener("input", slow);
  for (const el of [f.level, f.from, f.to]) el.addEventListener("change", refilter);
  const exportBtn = actionButton("Export CSV", async () => {
    const p = params();
    await download(`/v1/admin/events?${query({ ...p, offset: undefined, limit: undefined, format: "csv" })}`, "luwu-events.csv");
  });

  show(
    "Logs",
    "The ops log: what staff did, what nodes, alarms and billing did, and request errors at api.luwu.dev. Kept 90 days (staff and alarm events a year). A kind ending in * matches a prefix.",
    kinds,
    h(
      "div",
      { class: "fields" },
      field("Kind", f.kind),
      field("Level", f.level),
      field("Node", f.relay),
      field("Account", f.account),
      field("Search", f.q),
      field("From", f.from),
      field("To", f.to),
      h("div", null, exportBtn),
    ),
    holder,
  );
  await load();
}
